import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { OdinFormLabel } from '@/components/ui/odin/form/odin-form-label'
import { OdinFormDescription } from '@/components/ui/odin/form/odin-form-description'
import { OdinFormError } from '@/components/ui/odin/form/odin-form-error'

export type OdinInputPasswordProps = Omit<React.ComponentProps<'input'>, 'type'> & {
  label?: string
  description?: string
  error?: string
  required?: boolean
}

export function OdinInputPassword({
  className,
  label,
  description,
  error,
  required,
  id,
  placeholder,
  ...props
}: OdinInputPasswordProps) {
  const { t } = useTranslation()
  const reactId = React.useId()
  const inputId = id || `input-${reactId}`
  const errorId = inputId + '-error'
  const [showPassword, setShowPassword] = React.useState(false)
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <OdinFormLabel htmlFor={inputId} required={required}>
          {label}
        </OdinFormLabel>
      )}
      {description && <OdinFormDescription>{description}</OdinFormDescription>}
      <div className="relative">
        <Input
          id={inputId}
          type={showPassword ? 'text' : 'password'}
          placeholder={placeholder ?? label}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className="h-10 px-3 pr-9"
          {...props}
        />
        <button
          type="button"
          aria-label={showPassword ? t('hidePassword') : t('showPassword')}
          aria-pressed={showPassword}
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
        >
          <OdinIcon aria-hidden icon={['far', showPassword ? 'eye-slash' : 'eye']} />
        </button>
      </div>
      <OdinFormError error={error} id={errorId} />
    </div>
  )
}
